import { z } from "zod";
/**
 * Company branding, floor themes and building style: the public look of a company
 * (docs/PHASE2_PLAN.md, "Company branding, floor themes, building style"). The company node owns
 * them (`org.company`, `org.floor`, `org.building` props); the strict building facade carries the
 * same fields to the world, which stores them on the placement for the 3D client.
 */
/** Hex color `#rrggbb`. */
export const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);
/**
 * A logo URL: an absolute https URL, or a path on the web origin (`/brands/<slug>/x.png`).
 * No `javascript:` or `data:` URLs: they end up in `src`/`href` of public pages.
 */
export const LogoUrlSchema = z
    .string()
    .max(500)
    .refine((v) => /^https:\/\/[^\s]+$/i.test(v) || /^\/(?!\/)[A-Za-z0-9._~\-/%]+$/.test(v), "must be an https URL or a path starting with /");
export const BrandLogoSchema = z
    .object({
    /** Wordmark for light backgrounds. */
    light: LogoUrlSchema,
    /** Wordmark for dark backgrounds. */
    dark: LogoUrlSchema,
    /** Square mark (symbol only): signs, flags, lobby wall. */
    mark: LogoUrlSchema,
    /** Square app icon: favicon and avatars. */
    icon: LogoUrlSchema,
})
    .strict();
export const BrandColorsSchema = z
    .object({ primary: HexColorSchema, onPrimary: HexColorSchema, accent: HexColorSchema })
    .strict();
export const CompanyBrandingSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    legalName: z.string().trim().min(1).max(200).optional(),
    website: z
        .string()
        .max(300)
        .regex(/^https:\/\/[^\s]+$/i)
        .optional(),
    logo: BrandLogoSchema,
    colors: BrandColorsSchema,
    tagline: z.string().trim().min(1).max(200).optional(),
})
    .strict();
export const FLOOR_THEMES = [
    "lobby",
    "software",
    "studio",
    "marketing",
    "accounting",
    "leadership",
    "generic",
];
export const FloorThemeSchema = z.enum(FLOOR_THEMES);
export const BUILDING_PRESETS = ["plaza", "office", "campus", "tower", "showroom"];
export const BUILDING_ENTRANCES = ["revolving", "sliding", "double"];
/** Facade material presets (the same names as the world material presets that suit a facade). */
export const FACADE_MATERIALS = [
    "plaster",
    "brick",
    "concrete",
    "glass",
    "metal",
    "stone",
];
export const BuildingStyleSchema = z
    .object({
    preset: z.enum(BUILDING_PRESETS),
    entrance: z.enum(BUILDING_ENTRANCES),
    garage: z.boolean(),
    /** Flag poles in front of the building (0 = none). */
    flags: z.number().int().min(0).max(6),
    canopy: z.boolean(),
    rooftopSign: z.boolean(),
    facade: z.enum(FACADE_MATERIALS),
})
    .strict();
export const DEFAULT_BUILDING_STYLE = {
    preset: "office",
    entrance: "sliding",
    garage: false,
    flags: 0,
    canopy: false,
    rooftopSign: false,
    facade: "plaster",
};
/** Turkish and English display names of the floor themes (default floor names, UI hints). */
export const FLOOR_THEME_LABELS = {
    lobby: { tr: "Lobi", en: "Lobby" },
    software: { tr: "Yazılım katı", en: "Software floor" },
    studio: { tr: "Stüdyo", en: "Studio" },
    marketing: { tr: "Pazarlama", en: "Marketing" },
    accounting: { tr: "Muhasebe", en: "Accounting" },
    leadership: { tr: "Yönetim", en: "Leadership" },
    generic: { tr: "Kat", en: "Floor" },
};
/**
 * What to show for a floor: its name, never "Floor N". The level is secondary text. An
 * unnamed floor falls back to the theme label, then to the level.
 */
export function floorDisplayName(floor, locale) {
    const name = floor.name?.trim();
    if (name)
        return name;
    if (floor.theme && floor.theme !== "generic")
        return FLOOR_THEME_LABELS[floor.theme][locale];
    return `${FLOOR_THEME_LABELS.generic[locale]} ${floor.level}`;
}
//# sourceMappingURL=branding.js.map